import { useMemo } from 'react';
import { FAST_USER_INTERACTION_DEBOUNCE } from '../../../../../constants';
import { useDebouncedValue } from '../../../../../hooks/useDebounceValue';
import { useHannibalQuery } from '../../../../../schema/hannibal/HannibalContext';
import {
  TopicSuggestionsDocument,
  SuggestionTopicFragment,
} from '../../../../../schema/hannibal/types.generated';
import { EMPTY_LIST } from '../../../../../util/EMPTY_LIST';
import { SuggestionData } from '../../types';
import { SuggestionList } from '../SuggestionList/SuggestionList';
import {
  CategoryView,
  CategoryLabel,
  ListContainer,
} from '../SuggestionsLayoutTemplate/SuggestionsLayoutTemplate.styles';
import { TabProps } from '../TabProps';

export const mapTopicsSuggestionsToSuggestionData = (
  topics: readonly SuggestionTopicFragment[],
): SuggestionData[] =>
  topics
    .filter((it): it is SuggestionTopicFragment => it.type !== 'SPECIALTY')
    .map(
      (it): SuggestionData => ({
        type: 'FilterSuggestion',
        id: `topic-${it.name}`,
        filters: { topic: { treatment: it.name } },
      }),
    );

export const TopicSuggestions = ({
  searchTerm,
  onChoose,
  trackingData,
  toggleSingleSuggestionView,
  showSingleView,
  onFocus,
  listIndex = 0,
}: TabProps) => {
  const query = useDebouncedValue(searchTerm, FAST_USER_INTERACTION_DEBOUNCE);
  const { data } = useHannibalQuery(TopicSuggestionsDocument, { variables: { query } });

  const topics = data?.locationSuggestions.suggestions ?? EMPTY_LIST;

  const topicSuggestions: SuggestionData[] = useMemo(() => {
    if (showSingleView) {
      return mapTopicsSuggestionsToSuggestionData(topics);
    }

    return mapTopicsSuggestionsToSuggestionData(topics).slice(0, 3);
  }, [showSingleView, topics]);

  if (topicSuggestions.length === 0) {
    return null;
  }

  const suggestions: SuggestionData[] = topicSuggestions;

  return (
    <CategoryView>
      {!showSingleView && <CategoryLabel>Specialisations & procedures</CategoryLabel>}
      <ListContainer onFocus={() => onFocus?.(listIndex)}>
        <SuggestionList
          suggestions={suggestions}
          onChoose={onChoose}
          trackingData={trackingData}
          searchTerm={searchTerm}
          toggleSingleSuggestionView={toggleSingleSuggestionView}
          showSingleView={showSingleView}
          suggestionType="Topics"
          indexIdentifier={listIndex}
        />
      </ListContainer>
    </CategoryView>
  );
};
